import { observer } from "mobx-react";
import { useTranslation } from "@/i18n";
import { ConfirmDialog } from "@/components/primitives/confirm-dialog";
import type { DiscardPromptProps } from "@/components/ui/unsaved-changes";

/** "Discard changes?" for a dialog or sheet with unsaved input (NG-2717). Provided
 *  app-wide in `root.tsx` through `DiscardPromptProvider`, and rendered by the guarded
 *  dialog inside its own popup — which is what makes base-ui nest it.
 *
 *  A `ConfirmDialog`, so it is non-dismissible the same way every confirmation is
 *  (Abir, 2026-10-13): Escape or a stray click must not decide for the person whether
 *  their work is kept, so only the two buttons settle it. */
export const DiscardChangesPrompt = observer(function DiscardChangesPrompt({
  open,
  onKeepEditing,
  onDiscard,
}: DiscardPromptProps) {
  const { t } = useTranslation();
  return (
    <ConfirmDialog
      role="alertdialog"
      stacked
      isOpen={open}
      isSubmitting={false}
      onCancel={onKeepEditing}
      onAccept={onDiscard}
      options={{
        title: t("common.unsaved_changes.title"),
        description: t("common.unsaved_changes.description"),
        confirmLabel: t("common.unsaved_changes.discard"),
        cancelLabel: t("common.unsaved_changes.keep_editing"),
        destructive: true,
      }}
    />
  );
});
